import { useState, useContext, useRef, useEffect, useCallback } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, History, Volume2, VolumeX, Users, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { celebrateWin, soundEffects } from "@/lib/celebrations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// WebSocket reconnection config
const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT_ATTEMPTS = 10;

export const CrashPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [loading, setLoading] = useState(false);
  const [gameState, setGameState] = useState("waiting");
  const [currentMultiplier, setCurrentMultiplier] = useState(1.0);
  const [countdown, setCountdown] = useState(0);
  const [history, setHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [connected, setConnected] = useState(false);
  const [hasBet, setHasBet] = useState(false);
  const [cashedOut, setCashedOut] = useState(false);
  const [livePlayers, setLivePlayers] = useState([]);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    soundEffects.setEnabled(soundEnabled);
  }, [soundEnabled]);

  // WebSocket connection with auto-reconnect
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    try {
      wsRef.current = new WebSocket(`${WS_URL}/ws/crash`);
      
      wsRef.current.onopen = () => {
        setConnected(true);
        setReconnectAttempts(0);
        console.log("Connected to Crash game");
        toast.success("Connected to game server");
      };
      
      wsRef.current.onclose = (event) => {
        setConnected(false);
        console.log("WebSocket closed:", event.code);
        
        // Auto-reconnect with exponential backoff
        if (reconnectAttempts < WS_MAX_RECONNECT_ATTEMPTS) {
          const delay = WS_RECONNECT_DELAY * Math.pow(1.5, reconnectAttempts);
          toast.error(`Connection lost. Reconnecting in ${Math.round(delay/1000)}s...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            connectWebSocket();
          }, delay);
        } else {
          toast.error("Unable to connect. Please refresh the page.");
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          console.error("Failed to parse message:", e);
        }
      };
    } catch (error) {
      console.error("WebSocket connection error:", error);
      setTimeout(connectWebSocket, WS_RECONNECT_DELAY);
    }
  }, [reconnectAttempts]);

  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket]);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case "state":
        setGameState(data.status);
        setCurrentMultiplier(data.multiplier);
        setHistory(data.history || []);
        break;
        
      case "waiting":
        setGameState("waiting");
        setCountdown(data.countdown);
        setCurrentMultiplier(1.0);
        setHasBet(false);
        setCashedOut(false);
        setLivePlayers([]);
        break;
        
      case "start":
        setGameState("running");
        setCountdown(0);
        soundEffects.playSpin();
        break;
        
      case "tick":
        setCurrentMultiplier(data.multiplier);
        break;
        
      case "crash":
        setGameState("crashed");
        setCurrentMultiplier(data.crash_point);
        setHistory(data.history || []);
        if (hasBet && !cashedOut) {
          toast.error(`Crashed at ${data.crash_point}x!`);
          soundEffects.playLoss();
        }
        refreshBalance();
        break;
        
      case "bet_placed":
        setLivePlayers(prev => [...prev, { name: data.player, amount: data.amount }]);
        break;
        
      case "bet_confirmed":
        setHasBet(true);
        setLoading(false);
        toast.success(`Bet placed: ₹${data.amount}`);
        break;
        
      case "cashout":
        setLivePlayers(prev => prev.map(p => 
          p.name === data.player ? { ...p, cashedOut: true, multiplier: data.multiplier } : p
        ));
        break;
        
      case "cashout_success":
        setCashedOut(true);
        setLoading(false);
        toast.success(`Cashed out at ${data.multiplier}x! Won ₹${data.winnings}`);
        celebrateWin(data.multiplier, data.winnings);
        refreshBalance();
        break;
        
      case "error":
        toast.error(data.message);
        setLoading(false);
        break;
        
      default:
        console.log("Unknown message type:", data.type);
    }
  }, [hasBet, cashedOut, refreshBalance]);

  // Draw game canvas
  const drawGame = useCallback((multiplier, crashed = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear
    ctx.fillStyle = "#0A0A0B";
    ctx.fillRect(0, 0, width, height);
    
    // Grid
    ctx.strokeStyle = "#1A1B1F";
    ctx.lineWidth = 1;
    for (let i = 0; i < 10; i++) {
      ctx.beginPath();
      ctx.moveTo(0, (height / 10) * i);
      ctx.lineTo(width, (height / 10) * i);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo((width / 10) * i, 0);
      ctx.lineTo((width / 10) * i, height);
      ctx.stroke();
    }
    
    // Draw curve
    if (gameState === "running" || crashed) {
      ctx.beginPath();
      ctx.moveTo(50, height - 50);
      
      const points = [];
      for (let x = 0; x <= width - 100; x += 5) {
        const progress = x / (width - 100);
        const y = height - 50 - (progress * (height - 100) * Math.log(multiplier) / 3);
        points.push({ x: x + 50, y: Math.max(50, y) });
      }
      
      const gradient = ctx.createLinearGradient(50, height - 50, width - 50, 50);
      gradient.addColorStop(0, crashed ? "#FF3B30" : "#00E701");
      gradient.addColorStop(1, crashed ? "#FF6B00" : "#00E701");
      
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      
      points.forEach((point, i) => {
        if (i === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
      
      // Rocket at end
      const lastPoint = points[points.length - 1];
      if (lastPoint && !crashed) {
        ctx.fillStyle = "#00E701";
        ctx.shadowColor = "#00E701";
        ctx.shadowBlur = 20;
        ctx.beginPath();
        ctx.arc(lastPoint.x, lastPoint.y, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }
    
    // Center text
    if (gameState === "waiting") {
      ctx.fillStyle = "#FFD600";
      ctx.font = "bold 32px Rajdhani";
      ctx.textAlign = "center";
      ctx.fillText(`Starting in ${countdown}s`, width / 2, height / 2);
      ctx.font = "18px Rajdhani";
      ctx.fillStyle = "#A1A1AA";
      ctx.fillText("Place your bets!", width / 2, height / 2 + 30);
    } else {
      ctx.fillStyle = crashed ? "#FF3B30" : "#00E701";
      ctx.font = "bold 64px Rajdhani";
      ctx.textAlign = "center";
      ctx.fillText(`${multiplier.toFixed(2)}x`, width / 2, height / 2);
      
      if (crashed) {
        ctx.fillStyle = "#FF3B30";
        ctx.font = "bold 24px Rajdhani";
        ctx.fillText("CRASHED!", width / 2, height / 2 + 40);
      }
    }
  }, [gameState, countdown]);

  useEffect(() => {
    drawGame(currentMultiplier, gameState === "crashed");
  }, [currentMultiplier, gameState, drawGame]);

  // Countdown timer
  useEffect(() => {
    if (gameState === "waiting" && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, countdown]);

  const placeBet = () => {
    if (!connected) {
      toast.error("Not connected to game server");
      return;
    }
    if (gameState !== "waiting") {
      toast.error("Wait for next round");
      return;
    }
    if (betAmount <= 0 || betAmount > (user?.balance || 0)) {
      toast.error("Invalid bet amount");
      return;
    }
    
    setLoading(true);
    wsRef.current?.send(JSON.stringify({
      action: "bet",
      user_id: user?.user_id,
      amount: betAmount,
      token: token
    }));
  };

  const cashOut = () => {
    if (!connected || gameState !== "running" || !hasBet || cashedOut) return;
    
    setLoading(true);
    wsRef.current?.send(JSON.stringify({
      action: "cashout",
      token: token
    }));
  };

  // Manual reconnect
  const handleManualReconnect = () => {
    setReconnectAttempts(0);
    if (wsRef.current) {
      wsRef.current.close();
    }
    connectWebSocket();
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#00E701]/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-[#00E701]" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white font-['Rajdhani']" data-testid="crash-title">Crash</h1>
              <p className="text-[#A1A1AA] text-xs md:text-sm">Real-time multiplayer crash game!</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge 
              variant={connected ? "default" : "destructive"} 
              className={`flex items-center gap-1 cursor-pointer ${!connected ? 'hover:bg-red-600' : ''}`}
              onClick={!connected ? handleManualReconnect : undefined}
            >
              {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {connected ? "Live" : "Reconnect"}
            </Badge>
            <button 
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 rounded-lg bg-white/5 text-white/60 hover:text-white touch-manipulation"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Game Canvas */}
          <div className="lg:col-span-2">
            <div className="p-3 md:p-4 rounded-2xl bg-[#1A1B1F] border border-white/10">
              <canvas 
                ref={canvasRef}
                width={600}
                height={400}
                className="w-full rounded-xl"
                data-testid="crash-canvas"
              />
              
              {/* Live Players */}
              {livePlayers.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-[#A1A1AA]" />
                    <span className="text-[#A1A1AA] text-sm">Live Bets</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {livePlayers.slice(0, 10).map((p, i) => (
                      <Badge 
                        key={i}
                        variant="outline"
                        className={`text-xs ${p.cashedOut ? "border-[#00E701] text-[#00E701]" : "border-white/20 text-white"}`}
                      >
                        {p.name} - ₹{p.amount}
                        {p.cashedOut && ` @${p.multiplier}x`}
                      </Badge>
                    ))}
                    {livePlayers.length > 10 && (
                      <Badge variant="outline" className="text-xs border-white/20 text-white/60">
                        +{livePlayers.length - 10} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount</label>
              <Input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                disabled={loading || hasBet}
                className="h-12 bg-black/20 border-white/10 text-white font-mono text-lg touch-manipulation"
                data-testid="bet-input"
              />
              <div className="grid grid-cols-4 gap-2 mt-2">
                {[50, 100, 500, 1000].map(amt => (
                  <Button
                    key={amt}
                    variant="outline"
                    size="sm"
                    onClick={() => setBetAmount(amt)}
                    disabled={loading || hasBet}
                    className="border-white/10 text-white/60 hover:text-white text-xs h-10 touch-manipulation"
                  >
                    ₹{amt}
                  </Button>
                ))}
              </div>
            </div>

            {/* Bet or Cashout Button */}
            {!hasBet ? (
              <Button
                onClick={placeBet}
                disabled={loading || gameState !== "waiting" || betAmount <= 0 || betAmount > (user?.balance || 0)}
                className="w-full h-14 bg-[#00E701] hover:bg-[#00C201] text-black font-bold text-lg touch-manipulation"
                data-testid="bet-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Placing...
                  </div>
                ) : gameState === "waiting" ? (
                  <>
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Bet ₹{betAmount}
                  </>
                ) : (
                  "Wait for next round"
                )}
              </Button>
            ) : !cashedOut ? (
              <Button
                onClick={cashOut}
                disabled={loading || gameState !== "running"}
                className="w-full h-14 bg-[#FFD600] hover:bg-[#E6C200] text-black font-bold text-lg animate-pulse touch-manipulation"
                data-testid="cashout-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Cashing out...
                  </div>
                ) : (
                  `CASH OUT ${currentMultiplier.toFixed(2)}x`
                )}
              </Button>
            ) : (
              <div className="w-full h-14 bg-[#00E701]/20 rounded-xl flex items-center justify-center text-[#00E701] font-bold">
                Cashed Out!
              </div>
            )}

            {/* History */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2 text-sm">
                <History className="w-4 h-4" /> Recent Crashes
              </h3>
              <div className="flex flex-wrap gap-2">
                {history.slice(0, 15).map((h, i) => (
                  <span 
                    key={i}
                    className={`px-2 py-1 rounded text-xs font-mono ${
                      h.crash_point >= 2 ? 'bg-[#00E701]/20 text-[#00E701]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'
                    }`}
                  >
                    {h.crash_point}x
                  </span>
                ))}
                {history.length === 0 && (
                  <span className="text-[#52525B] text-sm">No games yet</span>
                )}
              </div>
            </div>

            {/* Game Info */}
            <div className="p-4 rounded-xl bg-white/5 text-[#A1A1AA] text-xs md:text-sm space-y-2">
              <p>• Bet during countdown phase</p>
              <p>• Cash out before crash to win</p>
              <p>• Real-time multiplayer game</p>
              <p>• Max win: 100x your bet</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
